<template>
	<div class="job-widget" :type="type" @click="handleClick">
		<div class="job-header">
			<!-- @slot Used to display job widget header <br/> Note : use html element with 'title' class to display header -->
			<slot name="header" />
		</div>
		<div class="job-body">
			<!-- @slot Used to display widget content<br/> e.g. LabelValue component is used display key value data -->
			<slot />
		</div>
		<div class="job-footer">
			<!-- @slot Used to display widget footer <br/> e.g. ProgressBar is used display progress of stages-->
			<slot name="footer" />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
/**
 * Job widget allows a user to visualise and get high level coverage and context of all jobs running in one pipeline.
 */
export default defineComponent({
	name: "JobWidget",
	props: {
		/**
		 * 'success' | 'error' | 'inProgress' | 'warning' | 'disabled'
		 */
		// eslint-disable-next-line vue/require-default-prop
		type: {
			type: String,
			validator(value) {
				return ["success", "error", "inProgress", "warning", "disabled"].includes(value);
			}
		}
	},
	methods: {
		handleClick(e) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			this.$emit("click", e);
		}
	}
});
</script>
<style lang="scss">
@use "sass:map";

$job-widget-background-color: var(--dashboard-element-light);
$job-widget-header-font-color: var(--dashboard-font-dark);
$job-widget-header-icon-color: var(--dashboard-icon-light);
$job-widget-disabled-text-color: var(--dashboard-font-light);
$job-widget-colors: (
	"error": var(--dashboard-danger-gradient),
	"warning": var(--dashboard-warning-gradient),
	"disabled": var(--dashboard-element-light)
);
$hover: var(--dashboard-element-dark);
$icon-hover: var(--dashboard-primary);
$focus: var(--dashboard-primary);

$job-widget-types: "success", "error", "warning", "inProgress", "disabled";
$job-widget-header-height: 20px;
$job-widget-min-width: 260px;
$job-widget-max-width: 520px;
.job-widget {
	display: flex;
	flex-direction: column;
	min-width: $job-widget-min-width;
	max-width: $job-widget-max-width;
	// justify-content: space-between;
	background-color: $job-widget-background-color !important;
	// height: fit-content;
	position: relative;
	cursor: pointer;
	min-height: 182px;

	@each $type in $job-widget-types {
		&[type="#{$type}"] {
			background: map.get($job-widget-colors, $type);
		}
	}

	border-radius: 8px;
	color: $job-widget-header-font-color;

	&[type="disabled"] {
		> .job-header {
			color: $job-widget-disabled-text-color !important;
		}

		.label {
			color: $job-widget-disabled-text-color !important;
		}

		.value {
			color: $job-widget-disabled-text-color !important;
		}
	}

	&:active,
	&:hover:not(:active) {
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: $hover;
			border-radius: 8px;
		}
	}

	&:active {
		&:before {
			opacity: 0.08;
			cursor: pointer;
		}
	}

	&:hover:not(:active) {
		&:before {
			opacity: 0.04;
		}
	}

	&:focus {
		box-shadow: 0 0 0 2px $focus;
		outline: none;
	}

	> .job-header {
		// width: 100%;
		display: flex;
		align-items: center;
		font-weight: 500;
		font-size: 18px;
		justify-content: space-between;
		word-break: break-all;
		padding-right: 16px;

		.title {
			padding: 16px;
			font-size: 18px;
			line-height: 18px;
			max-width: calc(100% - 50px);
		}
		//overriding icon container!
		.icon-container {
			// height: 50px;
			// width: 52px;
			&:hover:not([disabled]) {
				& > [type="outlined"] > svg {
					stroke: $icon-hover;
				}

				& > [type="filled"] > svg {
					fill: $icon-hover;
				}
			}
			.icon[type="filled"] svg {
				fill: $job-widget-header-icon-color;
			}
		}
	}

	> .job-body {
		padding-left: 16px;
		padding-right: 16px;

		display: flex;
		flex-direction: column;
		// gap: 8px;

		min-height: 58px;

		> * {
			margin: 4px 0px 4px 0px;
		}

		> :first-child {
			margin: 0px 0px 4px 0px;
		}

		> :last-child {
			margin: 4px 0px 0px 0px;
		}
	}
	> .job-footer {
		margin-top: auto;
		padding: 16px;
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
		grid-gap: 10px;
		align-items: end;
	}
}

.job-widget-container {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(325px, 1fr));
	// grid-template-rows: repeat(auto-fill, minmax(325px, 1fr));
	grid-gap: 12px;
	padding: 0px 8px 0px 8px;
	// padding: 24px;
}

@media (max-width: 575px) {
	.job-widget-container {
		grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
	}
}
</style>
